/**
 * The `{{ }}` vocabulary an agent's template fields offer each other.
 *
 * Unlike a trigger's, it is derived rather than fetched: an agent's variables
 * ARE the placeholder names its own text carries, so the catalog is read back
 * out of the form. Two consequences follow, both deliberate:
 *
 * - A field's own tokens are in the catalog it is given, so a brand-new name
 *   is never flagged as unknown. For a trigger an unrecognised path is a typo;
 *   here it is how a variable is created.
 * - The implicit rows a goal brings — the dataset it runs on, the collection it
 *   files into — are offered without anyone typing them, because
 *   `buildVariables` declares them at save time either way.
 */

import {
  type TemplatePath,
  TriggerTemplateCatalog,
} from "@/shared/domain/triggers";

import {
  collectPlaceholders,
  type FormState,
} from "../Agents/buildRequestTemplate";

import { type AgentFormValues } from "./agentFormSchema";

/**
 * Every variable the form currently declares, sorted by name, with the
 * author's default as the value each resolves to. A variable with no default
 * resolves to nothing yet, which is what `null` says.
 */
export function agentVariablePaths(
  values: AgentFormValues,
): readonly TemplatePath[] {
  return [...collectPlaceholders(values as FormState)]
    .sort()
    .map((path): TemplatePath => {
      const given = values.variableMeta[path]?.default ?? "";
      return {
        path,
        root: path.split(".", 1)[0],
        sample: given === "" ? null : given,
        // Every value is spliced into the body as text; the declared
        // `TemplateVariableType` is a launch-page picker hint, not a type here.
        type: "string",
        container: false,
      };
    });
}

/** {@link agentVariablePaths}, as the catalog `TemplateField` reads. */
export function agentVariableCatalog(
  values: AgentFormValues,
): TriggerTemplateCatalog {
  return TriggerTemplateCatalog.fromPaths(agentVariablePaths(values));
}
